import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Download,
    RefreshCw,
    ShieldCheck,
    Wallet,
    ChevronDown,
    Users,
    ArrowRight,
    Eye,
    EyeOff,
    Send,
    CreditCard,
    AlertTriangle,
    Lock,
    Sparkles,
    Star,
    Crown,
    Zap,
    TrendingUp,
    Gift,
    X,
    DollarSign,
    PiggyBank
} from 'lucide-react';

// Assuming these are your existing imports
import {
    userSelect,
    homeScreenStatSelect,
    allRemoteSettingsSelect,
    allWithdrawalSelect,
    exchangeSelect,
} from "../../../../redux/selectors/index";
import { notify, money, moneyStatus, formatDateTime, capitalizeFirstLetter } from "../../../../utility/Helper1";
import { exchangeActions } from '../../../../redux/actions';
import PinAuth from '../../../../utility/customComponents/PinAuth';
import WrapUpP2P from '../../p2p/layouts/WrapUpP2p';
import { useLocation } from 'react-router-dom';

const WalletPage = () => {
    const finance = useSelector(homeScreenStatSelect);
    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const dispatch = useDispatch();

    const [securityObj, setSecurityObj] = useState({});
    const [currencySelected, setCurrencySelected] = useState(finance?.[0]);
    const [amount, setAmount] = useState("");
    const [withdrawalIsActive, setWithdrawalIsActive] = useState(false);
    const [selectedWallet, setSelectedWallet] = useState(1);
    const [balanceVisible, setBalanceVisible] = useState(true);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [hoveredWallet, setHoveredWallet] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const op_se = site_settings.operational_settings;
    const location = useLocation();

    const walletSources = [
        {
            title: "Main Balance",
            column: "balance",
            id: 1,
            useThis: true,
            icon: <Wallet className="h-8 w-8" />,
            description: "Your primary wallet balance",
            theme: "blue",
            gradient: "from-blue-500 via-blue-600 to-purple-700",
            bgGradient: "from-blue-500/10 via-blue-600/5 to-purple-700/10",
            borderGradient: "from-blue-500/50 to-purple-700/50",
            glowColor: "blue-500/25"
        },
        {
            title: "Commission",
            column: "commission",
            id: 2,
            useThis: Number(op_se?.useReferralBonus) == 1,
            icon: <Crown className="h-8 w-8" />,
            description: "Earned referral bonuses & rewards",
            theme: "gold",
            gradient: "from-yellow-400 via-amber-500 to-orange-600",
            bgGradient: "from-yellow-400/10 via-amber-500/5 to-orange-600/10",
            borderGradient: "from-yellow-400/50 to-orange-600/50",
            glowColor: "yellow-400/25"
        },
    ];

    const processWithdrawal = (e) => {
        e.preventDefault();

        if (withdrawalIsActive) return;

        if (isNaN(amount) || amount == 0) {
            return false;
        }

        setWithdrawalIsActive(true);

        const walletChosenColumn = walletSources?.[selectedWallet - 1]?.["id"];

        dispatch(
            exchangeActions.onPostExchange(
                {
                    amount: amount,
                    ...(securityObj ?? {}),
                    currency: currencySelected?.type,
                },
                `p2p/withdrawal/new/${walletChosenColumn}`,
                (response) => {
                    notify(response.msg, response.code);
                    if (response.code == 1) {
                        setAmount("");
                        setShowModal(false);
                    }
                    setWithdrawalIsActive(false);
                },
            ),
        );
    };

    const updateCurrencySelected = (typeOfSelected) => {
        const selected = finance?.filter((ite, inde) => ite.type == typeOfSelected)?.[0] ?? [];
        setCurrencySelected(selected);
    };

    const changeSelectedWallet = (value) => {
        setSelectedWallet(value);
    };

    const openWithdrawModal = (walletId) => {
        setSelectedWallet(walletId);
        setShowModal(true);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const commission = urlParams.get('commission');
        console.log('commission:', commission);

        if (commission) {
            // setTimeout(() => {
                openWithdrawModal(2);
            // }, 50);
        }
    }, [location.search]); // watch for changes in query string

    const selectedWalletData = walletSources?.find(w => w.id === selectedWallet);
    const availableBalance = finance?.filter((finItem) => finItem?.type == currencySelected?.type)?.[0]?.[selectedWalletData?.column] ?? 0;

    // Calculate total balance
    const totalBalance = finance?.reduce((total, finItem) => {
        if (finItem?.type === currencySelected?.type) {
            return total + (finItem.balance || 0) + (finItem.commission || 0);
        }
        return total;
    }, 0) || 0;

    return (
        <WrapUpP2P allowAutoPadding={true}>
            <div className="min-h-screen">
                {/* Animated Background Effects */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-yellow-400/10 to-amber-600/10 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-blue-400/10 to-purple-600/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-gradient-to-r from-green-400/5 to-emerald-600/5 rounded-full blur-3xl animate-pulse delay-2000"></div>
                </div>

                <div className="relative z-10 p-6">
                    <div className="max-w-6xl mx-auto">

                        {/* Wallet Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 mb-8">
                            {walletSources?.map((wallet, index) => {
                                if (!wallet.useThis) return null;

                                const showBalance = finance?.filter((finItem) => finItem?.type == currencySelected?.type)?.[0]?.[wallet?.column] ?? "";
                                const isHovered = hoveredWallet === wallet?.id;

                                return (
                                    <div
                                        key={index}
                                        className="relative group cursor-pointer transition-all duration-500 transform hover:scale-105"
                                        onMouseEnter={() => setHoveredWallet(wallet?.id)}
                                        onMouseLeave={() => setHoveredWallet(null)}
                                        onClick={() => openWithdrawModal(wallet?.id)}
                                    >
                                        {/* Glow Effect */}
                                        <div className={`absolute inset-0 bg-gradient-to-r ${wallet.gradient} rounded-3xl blur-xl opacity-0 transition-opacity duration-500 ${isHovered ? 'opacity-30' : ''}`}></div>

                                        {/* Main Card */}
                                        <div className={`relative bg-gradient-to-br ${wallet.bgGradient} backdrop-blur-xl border-2 rounded-3xl p-8 transition-all duration-500 border-gray-600/30 hover:border-gray-500/50 h-full`}>
                                            {/* Icon Section */}
                                            <div className="flex items-center justify-between mb-6">
                                                <div className={`bg-gradient-to-r ${wallet.gradient} rounded-2xl p-4 shadow-lg`}>
                                                    <div className="text-white">
                                                        {wallet.icon}
                                                    </div>
                                                </div>

                                                {wallet.id === 2 && (
                                                    <div className="flex space-x-2">
                                                        <div className="bg-yellow-400/20 rounded-full p-2">
                                                            <Gift className="w-4 h-4 text-yellow-400" />
                                                        </div>
                                                        <div className="bg-purple-400/20 rounded-full p-2">
                                                            <TrendingUp className="w-4 h-4 text-purple-400" />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div className="space-y-4">
                                                <div>
                                                    <h3 className="text-2xl font-bold text-white mb-2">{wallet.title}</h3>
                                                    <p className="text-gray-400 text-sm">{wallet.description}</p>
                                                </div>

                                                {/* Balance Display */}
                                                <div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/50">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-gray-400 font-medium">Available Balance</span>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setBalanceVisible(!balanceVisible);
                                                            }}
                                                            className="p-1 text-gray-400 hover:text-yellow-400 transition-colors"
                                                        >
                                                            {balanceVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                    <div className="mt-2">
                                                        <span className="text-2xl font-bold text-white">
                                                            {balanceVisible && showBalance !== ""
                                                                ? money(showBalance, currencySelected?.type)
                                                                : balanceVisible ? "0.00" : '••••••'
                                                            }
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Withdrawal Button */}
                                                <button
                                                    className={`w-full flex items-center justify-center space-x-3 py-4 px-6 rounded-xl font-bold transition-all duration-300 bg-gradient-to-r ${wallet.gradient} text-white hover:shadow-xl transform hover:scale-105`}
                                                >
                                                    <Download className="w-5 h-5" />
                                                    <span>Withdraw</span>
                                                    <ArrowRight className="w-5 h-5" />
                                                </button>

                                                {/* Special Features */}
                                                <div className="space-y-2">
                                                    {wallet.id === 2 ? (
                                                        <>
                                                            <div className="flex items-center space-x-3 text-xs">
                                                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                                                <span className="text-green-400 font-medium">Referral Bonuses</span>
                                                            </div>
                                                            <div className="flex items-center space-x-3 text-xs">
                                                                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                                                                <span className="text-purple-400 font-medium">Performance Rewards</span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="flex items-center space-x-3 text-xs">
                                                                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                                                <span className="text-blue-400 font-medium">Trading Balance</span>
                                                            </div>
                                                            <div className="flex items-center space-x-3 text-xs">
                                                                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                                                                <span className="text-cyan-400 font-medium">Instant Withdrawals</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Hover Animation */}
                                            <div className={`absolute inset-0 rounded-3xl transition-all duration-500 ${isHovered ? 'bg-white/5' : ''}`}></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Sidebar Info */}
                        <div className="grid grid-cols-1 md:grid-cols-1 gap-6">

                            {/* Security Features */}
                            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/60 backdrop-blur-xl border border-gray-700/30 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                                    <Lock className="w-6 h-6 mr-3 text-yellow-400" />
                                    Security Features
                                </h3>

                                <div className="space-y-3">
                                    {[
                                        { icon: AlertTriangle, text: "Fraud Detection", status: "active" },
                                        { icon: Zap, text: "Instant Processing", status: "active" }
                                    ].map((feature, index) => (
                                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-700/20 rounded-xl">
                                            <feature.icon className="w-5 h-5 text-green-400" />
                                            <span className="text-white font-medium">{feature.text}</span>
                                            <span className="ml-auto w-2 h-2 bg-green-400 rounded-full"></span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal Overlay */}
                {showModal && (
                    <div style={{ zIndex: '9999999' }} className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
                            onClick={() => setShowModal(false)}
                        ></div>

                        {/* Modal Content */}
                        <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 fade-in duration-300">
                            <div className="bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl border border-gray-700/50 rounded-3xl p-8 relative overflow-hidden">
                                {/* Modal Background Effects */}
                                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/5 to-amber-600/5 rounded-3xl"></div>

                                {/* Close Button */}
                                <button
                                    style={{ zIndex: '9999999' }}
                                    onClick={() => setShowModal(false)}
                                    className="absolute top-6 right-6 p-3 bg-gray-700/50 hover:bg-gray-600/50 rounded-full transition-colors z-10"
                                >
                                    <X className="w-6 h-6 text-white" />
                                </button>

                                <div className="relative z-10">
                                    {/* Modal Header */}
                                    <div className="flex items-center mb-8">
                                        <div className="bg-gradient-to-r from-yellow-400 to-amber-500 rounded-2xl p-4 mr-4">
                                            <Download className="w-8 h-8 text-black" />
                                        </div>
                                        <div>
                                            <h2 className="text-3xl font-bold text-white">
                                                Withdraw from {selectedWalletData?.title}
                                            </h2>
                                            <p className="text-gray-400 mt-1">{selectedWalletData?.description}</p>
                                        </div>
                                    </div>

                                    <form onSubmit={processWithdrawal} className="space-y-8">
                                        {/* Currency Selection */}
                                        {finance?.length > 1 && (
                                            <div className="space-y-4">
                                                <label className="block text-lg font-semibold text-white">Select Currency</label>
                                                <div className="relative">
                                                    <select
                                                        onChange={(e) => updateCurrencySelected(e.target.value)}
                                                        className="block w-full pl-6 pr-12 py-6 text-lg bg-gray-700/30 border border-gray-600/50 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 rounded-2xl appearance-none text-white backdrop-blur-sm"
                                                    >
                                                        {finance?.map((item, index) => (
                                                            <option key={index} value={item.type} className="bg-gray-800">
                                                                {item?.abbr} - {item?.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                                                        <ChevronDown className="h-6 w-6" />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Amount Input */}
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <label className="block text-lg font-semibold text-white">Withdrawal Amount</label>
                                            </div>

                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={amount}
                                                    onChange={(e) => setAmount(e.target.value)}
                                                    className="w-full pl-6 pr-24 py-6 text-2xl font-bold bg-gray-700/30 border border-gray-600/50 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 rounded-2xl text-white backdrop-blur-sm"
                                                    placeholder="0.00"
                                                />
                                                <div className="absolute inset-y-0 right-0 pr-6 flex items-center">
                                                    <span className="text-xl font-semibold text-yellow-400">{currencySelected?.abbr}</span>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between text-gray-400">
                                                <span>Available: {balanceVisible ? money(availableBalance, currencySelected?.type) : '••••••'}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setAmount(availableBalance)}
                                                    className="px-4 py-2 bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-400 rounded-xl transition-colors font-medium"
                                                >
                                                    Max
                                                </button>
                                            </div>
                                        </div>

                                       
                                        <PinAuth resultingObjMethod={setSecurityObj} userData={userData} />


                                        {/* Submit Button */}
                                        <button
                                            type="submit"
                                            disabled={withdrawalIsActive || !amount || !securityObj}
                                            className={`w-full flex items-center justify-center space-x-4 py-6 px-8 rounded-2xl text-lg font-bold transition-all duration-300 ${withdrawalIsActive || !amount || !securityObj
                                                ? "bg-gray-600/50 text-gray-400 cursor-not-allowed"
                                                : "bg-gradient-to-r from-yellow-400 to-amber-500 text-black hover:shadow-2xl hover:shadow-yellow-400/25 transform hover:scale-105"
                                                }`}
                                        >
                                            {withdrawalIsActive ? (
                                                <>
                                                    <div className="animate-spin">
                                                        <RefreshCw className="w-6 h-6" />
                                                    </div>
                                                    <span>Processing Withdrawal...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="w-6 h-6" />
                                                    <span>Confirm Withdrawal</span>
                                                    <ArrowRight className="w-6 h-6" />
                                                </>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </WrapUpP2P>
    );
};

export default WalletPage;